import { type FormEvent, type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { Button, cn, Label } from '@/shared/ui';
import { useRequestRevision } from '../model/use-request-revision';

export interface RevisionRequestFormProps {
  readonly quoteRequestId: string;
  /** 화면에 보이는 최신 발행본 id — 서버가 최신이 아니면 CONFLICT 로 거절한다. */
  readonly versionId: string;
  readonly onSubmitted?: () => void;
  readonly onCancel?: () => void;
}

const REASON_MAX_LENGTH = 1_000;
const OUTDATED_NOTICE = '최신 견적서를 다시 불러왔습니다. 내용을 확인한 뒤 다시 요청해 주세요.';

/**
 * 수정 요청 사유 폼(견적 설계 §7-6). 재발행 충돌이면 사유를 지우지 않고 안내만 바꾼다 —
 * 위젯이 새 발행본 id 를 내려 주면 같은 사유로 다시 보낼 수 있다. 열리면 사유 입력란에 포커스를 둔다.
 */
export const RevisionRequestForm = ({
  quoteRequestId,
  versionId,
  onSubmitted,
  onCancel,
}: RevisionRequestFormProps): ReactElement => {
  const [reason, setReason] = useState('');
  const reasonId = useId();
  const errorId = `${reasonId}-error`;
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const { submit, isPending, fieldError, formError, isOutdated } = useRequestRevision();
  const message = fieldError ?? formError;

  useEffect(() => {
    reasonRef.current?.focus();
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const succeeded = await submit({ quoteRequestId, versionId, reason });

    if (succeeded) {
      setReason('');
      onSubmitted?.();
    }
  };

  return (
    <form
      aria-label="수정 요청"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className="flex flex-col gap-3 border border-foreground/15 p-5"
      noValidate
    >
      <h3 className="text-base font-semibold">수정 요청</h3>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={reasonId} isRequired>
          수정 요청 사유
        </Label>
        <textarea
          ref={reasonRef}
          id={reasonId}
          rows={4}
          required
          maxLength={REASON_MAX_LENGTH}
          value={reason}
          aria-invalid={fieldError ? true : undefined}
          aria-describedby={message ? errorId : undefined}
          className={cn(
            'w-full rounded-2xl border border-input bg-background px-4 py-2 text-sm transition-colors',
            'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            fieldError && 'border-destructive focus-visible:ring-destructive',
          )}
          placeholder="바꾸고 싶은 수량·조건·납기 등을 적어 주세요."
          onChange={(event) => setReason(event.target.value)}
        />
      </div>

      {isOutdated ? (
        <p role="status" className="text-sm text-amber-700">
          {OUTDATED_NOTICE}
        </p>
      ) : null}

      {message ? (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {message}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" isLoading={isPending}>
          수정 요청 보내기
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            닫기
          </Button>
        ) : null}
      </div>
    </form>
  );
};
